/*
 * @Author: Chao Yang
 * @Date: 2019-12-16 04:13:47
 * @LastEditor: Chao Yang
 * @LastEditTime: 2020-07-26 02:42:22
 */
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import * as Configruation from 'src/app/domain/model/configuration';
import * as Document from 'src/app/domain/model/document';
import { IDropDownBase } from 'src/app/domain/model/i-drop-down-base.model';
import { PreferenceProxy } from 'src/app/domain/proxy';
import { InfoService } from '../info';
import { Columns } from './columns.const';
import {
  IDashboardReadUnreadDateConfig,
  IDashboardReadUnreadOrderTypeConfig,
} from 'src/app/domain/model/document';
import { SessionService } from '../session';
import { environment } from 'src/environments/environment';

@Injectable()
export class PreferenceService {
  private columnDictionay = undefined;
  private documentCenterConfigurationEvent =
    new Subject<Document.IConfiguration>();
  private documentCenterConfiguration: Document.IConfiguration;

  constructor(
    private _preferenceProxy: PreferenceProxy,
    private _infoService: InfoService,
    private _sessionService: SessionService
  ) {}

  getAccountDetailColumns(): Observable<Document.IColumn[]> {
    return this._preferenceProxy
      .getAccountDetailConfiguration()
      .pipe(
        map((configuration) =>
          this.documentCenterConfiguration2Columns(configuration)
        )
      );
  }

  getAccountDetailActivityLastCheckDate(programId: number): Observable<number> {
    return this._preferenceProxy.getAccountDetailActivityLastCheckDate(
      programId
    );
  }

  getDashboardOfferingOpenClosedDataSource(): Observable<IDropDownBase[]> {
    return this._preferenceProxy.getDashboardOfferingOpenClosedDataSource();
  }

  getDashboardOfferingOpenClosedConfiguration(): Observable<
    Configruation.IConfiguration<number>
  > {
    return this._preferenceProxy.getDashboardOfferingOpenClosedConfiguration();
  }

  getDashboardOfferingSortConfiguration(): Observable<
    Configruation.IConfiguration<string>
  > {
    return this._preferenceProxy.getDashboardOfferingSortConfiguration();
  }

  getDocumentCenterColumns(): Observable<Document.IColumn[]> {
    return this._preferenceProxy
      .getDocumentCenterConfiguration()
      .pipe(
        map((configuration) =>
          this.documentCenterConfiguration2Columns(configuration)
        )
      );
  }

  getDocumentCenterColumnConfiguration(): Document.IColumn[] {
    const columns: Document.IColumn[] = [];
    this.documentCenterConfiguration.columns.forEach((c) => {
      let col = this.columnDictionay[c];
      if (col) {
        columns.push({
          field: col.field,
          header: col.header,
          selected: true,
        });
      }
    });
    Object.keys(this.columnDictionay).forEach((c) => {
      if (this.documentCenterConfiguration.columns.indexOf(c) === -1) {
        let col = this.columnDictionay[c];
        columns.push({
          field: col.field,
          header: col.header,
          selected: false,
        });
      }
    });
    return columns;
  }

  updateAccountDetailActivityLastCheckDate(
    programId: number,
    lastDate: Date
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.updateAccountDetailActivityLastCheckDate(
      programId,
      lastDate
    );
  }

  updateDocumentCenterColumnConfiguration(
    columns: Document.IColumn[]
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    this.documentCenterConfiguration.columns = columns
      .filter((c) => c.selected)
      .map((c) => c.field);
    if (this.documentCenterConfiguration.userId === null) {
      return this._preferenceProxy
        .createDocumentCenterConfiguration(this.documentCenterConfiguration)
        .pipe(
          map((r) => {
            this.documentCenterConfiguration.userId =
              this._infoService.getUserId();
            this.documentCenterConfigurationEvent.next(
              this.documentCenterConfiguration
            );
          })
        );
    } else {
      return this._preferenceProxy
        .updateDocumentCenterConfiguration(this.documentCenterConfiguration)
        .pipe(
          map((r) => {
            this.documentCenterConfigurationEvent.next(
              this.documentCenterConfiguration
            );
          })
        );
    }
  }

  updateDocumentCenterColumnConfigurationEvent(): Observable<
    Document.IColumn[]
  > {
    return this.documentCenterConfigurationEvent.pipe(
      map((configuration) =>
        this.documentCenterConfiguration2Columns(configuration)
      )
    );
  }

  documentCenterConfiguration2Columns(
    configuration: Document.IConfiguration
  ): Document.IColumn[] {
    if (this.columnDictionay === undefined) {
      const columnDict = {};
      Columns.forEach((c) => {
        if (environment.feature.documentNameVisible === true) {
          columnDict[c.field] = c;
        } else if (
          environment.feature.documentNameVisible === false &&
          c.field !== 'fileName'
        ) {
          columnDict[c.field] = c;
        }
      });
      this.columnDictionay = columnDict;
    }
    this.documentCenterConfiguration = configuration;
    const columns: Document.IColumn[] = [];
    configuration.columns.forEach((c) => {
      if (this.columnDictionay[c]) {
        columns.push(this.columnDictionay[c]);
      }
    });
    return columns;
  }

  createDashboardDocumentDateConfig(
    configuration: Document.IDashboardReadUnreadDateConfig
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.createDashboardDocumentDateConfig(
      configuration
    );
  }

  createDashboardOfferingOpenClosedConfig(
    configuration: Document.IDashboardReadUnreadDateConfig
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.createDashboardOfferingOpenClosedConfig(
      configuration
    );
  }

  updateDashboardDocumentDateConfig(
    configuration: Document.IDashboardReadUnreadDateConfig
  ) {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.updateDashboardDocumentDateConfig(
      configuration
    );
  }

  updateDashboardOfferingOpenClosedConfig(
    configuration: Document.IDashboardReadUnreadDateConfig
  ) {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.updateDashboardOfferingOpenClosedConfig(
      configuration
    );
  }

  getDashboardDocumentDateConfig(): Observable<IDashboardReadUnreadDateConfig> {
    return this._preferenceProxy.getDashboardDocumentDateConfig();
  }

  createDashboardDocumentSortConfig(
    configuration: Document.IDashboardReadUnreadOrderTypeConfig
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.createDashboardDocumentSortConfig(
      configuration
    );
  }

  createDashboardOfferingSortConfig(
    configuration: Document.IDashboardReadUnreadOrderTypeConfig
  ): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.createDashboardOfferingSortConfig(
      configuration
    );
  }

  updateDashboardDocumentSortConfig(
    configuration: Document.IDashboardReadUnreadOrderTypeConfig
  ) {
    if (this._sessionService.isShadow()) {
      return this._preferenceProxy.getShadowUserDefaultObservable();
    }
    return this._preferenceProxy.updateDashboardDocumentSortConfig(
      configuration
    );
  }

  updateDashboardOfferingSortConfig(
    configuration: Document.IDashboardReadUnreadOrderTypeConfig
  ) {
    return this._preferenceProxy.updateDashboardOfferingSortConfig(
      configuration
    );
  }

  getDashboardDocumentSortConfig(): Observable<IDashboardReadUnreadOrderTypeConfig> {
    return this._preferenceProxy.getDashboardDocumentSortConfig();
  }

  getDataSource() {
    return this._preferenceProxy.getDataSource();
  }

  setDraftDS() {
    return this._preferenceProxy.setDraftDS();
  }

  setPublishedDS() {
    return this._preferenceProxy.setPublishedDS();
  }

  setGlobalCurrency(globalCurrency: string) {
    return this._preferenceProxy.setGlobalCurrency(globalCurrency);
  }
}
